// engine/loop.ts — fixed 60Hz simulation decoupled from rAF.
// Timescale multiplies simulated time per real second (?speed=8 / __gl.setTimescale).
// Hitstop consumes simulated time without stepping the world, so a hit freeze is
// identical in sim-seconds at every timescale (the balance bot stays honest).

export const STEP = 1 / 60

export class Loop {
  timescale = 1
  fps = 60
  running = false
  /** Simulated seconds still owed to hitstop. */
  private stop = 0
  private acc = 0
  private last = 0
  private rafId = 0
  private frames = 0
  private fpsAcc = 0

  constructor(
    private readonly step: (dt: number) => void,
    private readonly render: () => void,
    private readonly onError?: (e: unknown) => void,
  ) {}

  start(): void {
    if (this.running) return
    this.running = true
    this.last = performance.now()
    this.acc = 0
    this.rafId = requestAnimationFrame(this.frame)
  }

  stop_(): void {
    this.running = false
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = 0
  }

  /** Freeze the simulation for `s` simulated seconds (impact punch). */
  addHitstop(s: number): void {
    if (s > this.stop) this.stop = s
  }

  get hitstopRemaining(): number {
    return this.stop
  }

  private frame = (now: number): void => {
    if (!this.running) return
    this.rafId = requestAnimationFrame(this.frame)

    let realDt = (now - this.last) / 1000
    this.last = now
    if (!Number.isFinite(realDt) || realDt < 0) realDt = 0
    // A backgrounded tab must not spend the whole night catching up.
    if (realDt > 0.25) realDt = 0.25

    this.fpsAcc += realDt
    this.frames++
    if (this.fpsAcc >= 0.5) {
      this.fps = this.frames / this.fpsAcc
      this.frames = 0
      this.fpsAcc = 0
    }

    this.acc += realDt * this.timescale
    const maxSteps = Math.max(8, Math.ceil(this.timescale * 8))
    let steps = 0
    try {
      while (this.acc >= STEP && steps < maxSteps) {
        this.acc -= STEP
        steps++
        if (this.stop > 0) {
          this.stop -= STEP
          continue
        }
        this.step(STEP)
      }
      if (this.acc > STEP * maxSteps) this.acc = 0 // shed the backlog, never spiral
      this.render()
    } catch (e) {
      this.onError?.(e)
      throw e
    }
  }
}
